import React, { useState } from 'react';
import { CheckCircle2, Circle } from 'lucide-react';

/**
 * Badge "Validé client" — affichage compact + toggle pour l'admin.
 *
 * Props:
 *  - valide: bool (état courant)
 *  - canToggle: bool (admin uniquement)
 *  - endpoint: string — chemin POST relatif à /api (ex: '/turpe/rapports/123/toggle-valide-client')
 *  - onUpdated: callback(newValue) — appelé après basculement réussi
 *  - size: 'sm' | 'md' (défaut 'md')
 */
const ValideClientBadge = ({ valide, canToggle = false, endpoint = '', onUpdated, size = 'md' }) => {
  const [busy, setBusy] = useState(false);
  const [val, setVal] = useState(!!valide);

  React.useEffect(() => { setVal(!!valide); }, [valide]);

  const toggle = async (e) => {
    e.stopPropagation();
    if (!canToggle || busy || !endpoint) return;
    setBusy(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api${endpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const data = await r.json();
        setVal(!!data.valide_client);
        if (onUpdated) onUpdated(!!data.valide_client);
      }
    } catch (_) { /* silent */ }
    finally { setBusy(false); }
  };

  const isSm = size === 'sm';
  const base = isSm ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-xs';
  const Icon = val ? CheckCircle2 : Circle;

  if (!canToggle && !val) return null; // côté Dashboard non-admin : pas de badge "non validé"

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={!canToggle || busy}
      className={`inline-flex items-center gap-1 rounded-full ${base} font-medium transition-colors ${
        val
          ? 'bg-green-100 text-green-700 hover:bg-green-200'
          : 'bg-[#FAFBFC] text-[#687175] border border-[#BFC6CC] hover:bg-[#F5F6F9]'
      } ${canToggle ? 'cursor-pointer' : 'cursor-default'} disabled:opacity-50`}
      data-testid="valide-client-badge"
      title={canToggle ? (val ? 'Cliquez pour retirer la validation client' : 'Cliquez pour marquer comme validé par le client') : (val ? 'Validé par le client' : '')}
    >
      <Icon className={isSm ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
      {val ? 'Validé client' : 'Non validé'}
    </button>
  );
};

export default ValideClientBadge;
